/**
 * Adapter: wires ProductStore state through the unified <CheckoutShell>.
 *
 * Responsibilities:
 *   1. Read cart items from redux + customer info / order notes from the
 *      product checkout context.
 *   2. Call buildProductCart() to produce a Cart in the shell's contract.
 *   3. Wire interactive callbacks:
 *      - onUpdateLineQuantity / onRemoveLine → existing redux thunks; on
 *        completion, recompute Cart from latest cartItems.
 *      - onSubmit → placeProductStoreOrder API; on success navigate to
 *        the routable confirmation page.
 *   4. Slot a customer info recap (with edit-back link) above the cart and
 *      an order notes recap below the cart when present.
 *
 * What stays domain-specific (NOT in this file):
 *   - Pre-checkout: product browsing, cart-add UI, customer info step.
 *   - <ProductOrderConfirmation> at /product-directory/orders/:uuid/confirmation.
 */

import React, { ReactNode, useMemo } from "react";
import { Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { CheckoutShell } from "@/modules/checkout/components/CheckoutShell";
import type {
  Cart,
  CartLine,
  SubmitInput,
  SubmitResult,
} from "@/modules/checkout/types";
import { generateInvoiceNumber } from "@/modules/checkout/utils/generateInvoiceNumber";
import { useAppDispatch, useAppSelector } from "@/app/hooks";

import { useProductCheckout } from "../ProductCheckout/ProductCheckoutContext";
import { buildProductCart } from "../../utils/buildProductCart";
import {
  removeCartItemAction,
  resetCart,
  updateCartItemAction,
} from "../../slices/productStoreCartSlice";
import { useStore } from "react-redux";
import type { RootState } from "@/app/rootReducer";
import { placeProductStoreOrder } from "../../api/placeProductStoreOrder/placeProductStoreOrderApi";
import type { PlaceProductStoreOrderRequest } from "../../api/placeProductStoreOrder/types";

export default function ProductCheckoutShellAdapter() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const cartItems = useAppSelector((state) => state.productStoreCart.items);
  const store = useStore<RootState>();
  const { state: checkoutState, goToStep } = useProductCheckout();
  const { customerInfo, orderNotes } = checkoutState;

  const cart: Cart = useMemo(
    () => buildProductCart({ cartItems }),
    [cartItems],
  );

  // After dispatching a cart-mutation thunk, the slice has already
  // committed. useAppSelector's closed-over `cartItems` is stale until the
  // next render — so we read directly from the store to build the fresh
  // Cart for the callback's return value.
  const refreshAndBuild = (): Cart =>
    buildProductCart({ cartItems: store.getState().productStoreCart.items });

  const onUpdateLineQuantity = async (
    line: CartLine,
    quantity: number,
  ): Promise<Cart> => {
    const cartItemId = (line.domainData?.cartItemId as number) ?? null;
    if (cartItemId == null) return cart;
    if (quantity < 1) {
      // Quantities below 1 should remove the line; CartSummary already
      // disables decrease at qty 1, so this is defensive.
      await dispatch(removeCartItemAction(cartItemId));
    } else {
      await dispatch(updateCartItemAction(cartItemId, quantity));
    }
    return refreshAndBuild();
  };

  const onRemoveLine = async (line: CartLine): Promise<Cart> => {
    const cartItemId = (line.domainData?.cartItemId as number) ?? null;
    if (cartItemId == null) return cart;
    await dispatch(removeCartItemAction(cartItemId));
    return refreshAndBuild();
  };

  const onSubmit = async (input: SubmitInput): Promise<SubmitResult> => {
    const invoiceNumber = generateInvoiceNumber();
    const dataDescriptor =
      input.payment?.kind === "token" ? input.payment.token.dataDescriptor : "";
    const dataValue =
      input.payment?.kind === "token" ? input.payment.token.dataValue : "";
    const cardExpiration =
      input.payment?.kind === "token"
        ? (input.payment.token.cardExpiration ?? "")
        : "";

    // Send the cart from localStorage in the payload — server resolves
    // product details fresh against canonical records.
    const items = store.getState().productStoreCart.items.map((line) => ({
      productUuid: line.product.id,
      quantity: line.quantity,
    }));

    const payload: PlaceProductStoreOrderRequest = {
      contactFirstName: customerInfo.firstName,
      contactLastName: customerInfo.lastName,
      contactEmail: customerInfo.email,
      contactPhone: customerInfo.phone,
      orderNotes: orderNotes ?? "",
      addressLine1: input.shipping?.addressLine1 ?? null,
      addressLine2: input.shipping?.addressLine2 ?? null,
      city: input.shipping?.city ?? null,
      state: input.shipping?.state ?? null,
      zipCode: input.shipping?.zipCode ?? null,
      dataDescriptor,
      dataValue,
      cardExpiration,
      invoiceNumber,
      items,
    };

    const response = await placeProductStoreOrder(payload);

    // Empty the localStorage cart so the next browse session starts clean.
    dispatch(resetCart());

    const successPath = `/product-directory/orders/${response.data.productStoreOrderUuid}/confirmation`;
    navigate(successPath);

    return {
      confirmationNumber: response.data.confirmationNumber,
      successPath,
    };
  };

  const aboveCart: ReactNode = (
    <div className="mb-6 flex items-center justify-between rounded-md border border-gray-200 dark:border-slate-700 px-4 py-3">
      <div className="text-sm">
        <p className="font-medium text-gray-900 dark:text-white">
          {customerInfo.firstName} {customerInfo.lastName}
        </p>
        <p className="text-muted-foreground">
          {customerInfo.email}
          {customerInfo.phone ? ` • ${customerInfo.phone}` : null}
        </p>
      </div>
      <button
        className="text-sm text-primary hover:text-primary/80 inline-flex items-center gap-1"
        onClick={() => goToStep(0)}
        type="button"
      >
        <Pencil className="h-3.5 w-3.5" />
        Edit
      </button>
    </div>
  );

  const belowCart: ReactNode = orderNotes ? (
    <div className="mt-4 rounded-md border border-gray-200 dark:border-slate-700 px-4 py-3 text-sm">
      <p className="text-muted-foreground text-xs mb-1">Order notes</p>
      <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
        {orderNotes}
      </p>
    </div>
  ) : null;

  return (
    <CheckoutShell
      cart={cart}
      onRemoveLine={onRemoveLine}
      onSubmit={onSubmit}
      onUpdateLineQuantity={onUpdateLineQuantity}
      paymentMode="inline"
      slots={{ aboveCart, belowCart }}
    />
  );
}
